import React, { useEffect, useState, useRef } from "react"
import { connect } from "react-redux"
import { setBreadcrumbItems } from '../../store/actions';
import {
  Card,
  CardBody,
  Col,
  Row,
  Badge,
  Button,
} from "reactstrap"

import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import interactionPlugin from "@fullcalendar/interaction"
import BootstrapTheme from "@fullcalendar/bootstrap"
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';

import { meetingsAPI, ticketsAPI, projetsAPI } from "../../services/api"
import { useAuth } from "../../contexts/AuthContext"

const Calender = props => {
  document.title = "Calendrier Dynamique | Ticket Management";

  const { user } = useAuth()
  const isClient = user?.role === 'client'

  const [allEvents, setAllEvents] = useState([])
  const [displayEvents, setDisplayEvents] = useState([])
  const [activeFilter, setActiveFilter] = useState(null) // null | 'meeting' | 'ticket' | 'project'
  const [loading, setLoading] = useState(true)
  const calendarRef = useRef();

  useEffect(() => {
    fetchCalendarData()
    props.setBreadcrumbItems('Calendrier', [
      { title: "Dashboard", link: "/dashboard" },
      { title: "Calendrier", link: "#" },
    ])
  }, [])

  const fetchCalendarData = async () => {
    setLoading(true)
    try {
      const [meetRes, ticketRes, projRes] = await Promise.all([
        meetingsAPI.getAll(),
        ticketsAPI.getAll(),
        projetsAPI.getAll()
      ])

      // Vérifier que les données sont des tableaux (gérer la pagination potentielle)
      const meetings = Array.isArray(meetRes.data) ? meetRes.data : (meetRes.data?.data || []);
      const tickets = Array.isArray(ticketRes.data) ? ticketRes.data : (ticketRes.data?.data || []);
      const projects = Array.isArray(projRes.data) ? projRes.data : (projRes.data?.data || []);

      const meetingEvents = meetings.map(m => ({
        id: `meet-${m.id}`,
        title: `📞 ${m.titre}`,
        start: m.date_heure,
        end: m.date_heure ? new Date(new Date(m.date_heure).getTime() + (m.duree_minutes || 60) * 60000) : null,
        className: 'bg-primary text-white',
        extendedProps: { type: 'meeting', data: m }
      }))

      const ticketEvents = tickets.map(t => {
        // Calcul de la durée en jours (8h = 1 jour)
        const durationDays = t.heures_estimees ? Math.ceil(t.heures_estimees / 8) : 1;

        let startDate, endDate;

        if (t.deadline) {
          // Si on a une deadline, on finit à la deadline
          const d = new Date(t.deadline);
          // On ajoute 1 jour car 'end' est exclusif dans FullCalendar pour allDay
          endDate = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
          startDate = new Date(d.getFullYear(), d.getMonth(), d.getDate() - (durationDays - 1));
        } else {
          // Sinon on part de la date d'ouverture ou création
          const d = new Date(t.opened_at || t.created_at);
          startDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
          endDate = new Date(d.getFullYear(), d.getMonth(), d.getDate() + durationDays);
        }

        const assignedName = t.assigned_to?.name || t.assignedTo?.name || "Non assigné";

        return {
          id: `ticket-${t.id}`,
          title: `🎫 ${t.titre} (${assignedName})`,
          start: startDate,
          end: endDate,
          allDay: true,
          className: t.statut === 'resolu' ? 'bg-success text-white' : 'bg-danger text-white',
          extendedProps: { type: 'ticket', data: t }
        };
      })

      const projectEvents = projects.filter(p => p.date_fin_prevue).map(p => ({
        id: `proj-${p.id}`,
        title: `📁 Projet: ${p.nom}`,
        start: p.date_fin_prevue,
        allDay: true,
        className: 'bg-info text-white',
        extendedProps: { type: 'project', data: p }
      }))

      setAllEvents([...meetingEvents, ...ticketEvents, ...projectEvents])
      setDisplayEvents([...meetingEvents, ...ticketEvents, ...projectEvents])
    } catch (error) {
      console.error("Erreur chargement calendrier", error)
    } finally {
      setLoading(false)
    }
  }

  // Gérer le filtrage
  const toggleFilter = (type) => {
    if (activeFilter === type) {
      setActiveFilter(null)
      setDisplayEvents(allEvents)
    } else {
      setActiveFilter(type)
      setDisplayEvents(allEvents.filter(e => e.extendedProps.type === type))
    }
  }

  const handleEventClick = (arg) => {
    if (isClient) return; // Pas de redirection pour les clients

    const { type, data } = arg.event.extendedProps;
    if (type === 'meeting') {
      window.location.href = `/meetings`;
    } else if (type === 'ticket') {
      window.location.href = `/tickets/${data.id}`;
    } else if (type === 'project') {
      window.location.href = `/projets/${data.id}`;
    }
  }

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="card-title">Calendrier Général</h4>
                <div className="d-flex gap-3">
                  <Badge
                    color="primary"
                    className={`p-2 cursor-pointer ${activeFilter && activeFilter !== 'meeting' ? 'opacity-50' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleFilter('meeting')}
                  >
                    <i className="mdi mdi-video me-1"></i> Réunions {activeFilter === 'meeting' && '✓'}
                  </Badge>
                  <Badge
                    color="danger"
                    className={`p-2 cursor-pointer ${activeFilter && activeFilter !== 'ticket' ? 'opacity-50' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleFilter('ticket')}
                  >
                    <i className="mdi mdi-alert-circle me-1"></i> Échéances Tickets {activeFilter === 'ticket' && '✓'}
                  </Badge>
                  <Badge
                    color="info"
                    className={`p-2 cursor-pointer ${activeFilter && activeFilter !== 'project' ? 'opacity-50' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleFilter('project')}
                  >
                    <i className="mdi mdi-briefcase me-1"></i> Fin de Projets {activeFilter === 'project' && '✓'}
                  </Badge>
                  {activeFilter && (
                    <Button
                      color="link"
                      size="sm"
                      onClick={() => { setActiveFilter(null); setDisplayEvents(allEvents); }}
                      className="p-0 text-muted"
                    >
                      Tout afficher
                    </Button>
                  )}
                </div>
              </div>

              {loading ? (
                <div className="text-center p-5">
                  <div className="spinner-border text-primary" role="status"></div>
                  <p className="mt-2">Chargement du calendrier...</p>
                </div>
              ) : (
                <FullCalendar
                  plugins={[
                    BootstrapTheme,
                    dayGridPlugin,
                    timeGridPlugin,
                    interactionPlugin,
                    listPlugin
                  ]}
                  handleWindowResize={true}
                  themeSystem="bootstrap"
                  headerToolbar={{
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
                  }}
                  events={displayEvents}
                  editable={false}
                  selectable={true}
                  eventClick={handleEventClick}
                  ref={calendarRef}
                  initialView="dayGridMonth"
                  locale="fr"
                  buttonText={{
                    today: "Aujourd'hui",
                    month: "Mois",
                    week: "Semaine",
                    day: "Jour",
                    list: "Liste"
                  }}
                />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  )
}

export default connect(null, { setBreadcrumbItems })(Calender)
